import { config } from "../config";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type GetJwtFn = () => Promise<string | null>;

let getJwtFn: GetJwtFn = async () => null;
let activeMerchantId: string | null = null;

export function setApiAuth(fn: GetJwtFn) {
  getJwtFn = fn;
}

export function setActiveMerchant(id: string | null) {
  activeMerchantId = id;
}

export async function api<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const jwt = await getJwtFn();

  // Replace :merchantId placeholder with active merchant
  let resolvedPath = path;
  if (path.includes(":merchantId")) {
    if (!activeMerchantId) {
      throw new ApiError(400, "No active merchant selected");
    }
    resolvedPath = path.replace(":merchantId", activeMerchantId);
  }

  const url = `${config.apiUrl}${resolvedPath}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (jwt) {
    headers["Authorization"] = `Bearer ${jwt}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({ error: "Request failed" }));
    throw new ApiError(res.status, errorBody.error || `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

/* ---- Convenience methods ---- */

export const apiGet = <T>(path: string) => api<T>("GET", path);
export const apiPost = <T>(path: string, body?: unknown) => api<T>("POST", path, body);
export const apiPut = <T>(path: string, body?: unknown) => api<T>("PUT", path, body);
export const apiDelete = <T>(path: string) => api<T>("DELETE", path);

/* ---- Merchant-scoped shortcuts ---- */

const m = (path: string) => `/api/merchants/:merchantId${path}`;

export const endpoints = {
  catalog: m("/catalog"),
  inflate: m("/catalog/inflate"),
  restore: m("/catalog/restore"),
  job: (jobId: string) => m(`/catalog/jobs/${jobId}`),
  createOrder: m("/orders"),
  payCash: (orderId: string) => m(`/orders/${orderId}/pay/cash`),
  pair: m("/terminal/pair"),
  pairStatus: (codeId: string) => m(`/terminal/pair/${codeId}`),
  terminalCheckout: m("/terminal/checkout"),
  checkoutStatus: (checkoutId: string) => m(`/terminal/checkout/${checkoutId}`),
  cancelCheckout: (checkoutId: string) => m(`/terminal/checkout/${checkoutId}/cancel`),
  settings: m("/settings"),
  integrityCheck: m("/integrity/check"),
  integrityLatest: m("/integrity/latest"),
  squareConnect: "/api/auth/square/connect",
  squareDisconnect: (appMerchantId: string) => `/api/auth/square/${appMerchantId}`,
} as const;
